import React from 'react';
import { FormGroup } from 'reactstrap';
import moment from 'moment';
import { faCalendarTimes } from '@fortawesome/free-solid-svg-icons';
import { getInputFormat } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import SlideForm from '../common/forms/SlideForm';
import {
    FormLabel, Input, onFieldChange, toasty,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { EmployeeCertification } from './EmployeeCertification';

export class EmployeeCertificationForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            errors: {},
            endDateError: '',
            employeeCertification:
                this.props.employeeCertification ?? new EmployeeCertification(),
            certifications: [],
            isValidated: false,
            attachmentFile: null,
            fileInputKey: Date.now(),

            showExpirationDate: false,
            showProofUpload: false,

            loading: true,
            saving: false,
            isNew: !(this.props.employeeCertification?.id),
            formTitle: '',
            employeeCertifications: this.props.employeeCertifications ?? [],
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onCertificationChanged = this.onCertificationChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    showHideFieldsBasedOnCertification = (certificationId, certifications) => {
        const selectedCertification = certifications.find((c) => c.id === certificationId);

        if (!selectedCertification) {
            this.setState({
                showExpirationDate: false,
                showProofUpload: false,
            });
            return;
        }

        this.setState({
            showExpirationDate: !!selectedCertification.willExpire,
            showProofUpload: !!selectedCertification.proofRequired,
        });
    };

    onChange = onFieldChange;

    onClose(response) {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onCertificationChanged = (selection) => {
        const { certifications, employeeCertification } = this.state;
        employeeCertification.certificationId = selection?.id ?? null;

        this.setState({ employeeCertification }, () => {
            this.showHideFieldsBasedOnCertification(
                employeeCertification.certificationId,
                certifications,
            );
        });
    };

    onCertificationBlur = () => {
        const { employeeCertification, certifications } = this.state;
        this.showHideFieldsBasedOnCertification(
            employeeCertification.certificationId,
            certifications,
        );
    };

    onDelete = async () => {
        const { employeeCertification } = this.state;
        const url = ApiRoutes.employeeCertification.delete(employeeCertification.id);

        const fetchType = httpClient.types.delete;

        const response = await httpClient.andGetResponse(
            fetchType,
            url,
            employeeCertification,
            'Error deleting Certification',
            () => this.setState({ saving: false }),
        );

        if (response) {
            toasty.success(
                'Employee Certification deleted',
                'Employee Certification deleted successfully.',
            );
            if (this.props.onSaveCallback) {
                this.props.onSaveCallback();
            }
            this.onClose(response);
        }
    };

    handleDateChange = (e) => {
        const { name, value } = e.target;
        this.setState(
            (state) => ({
                employeeCertification: {
                    ...state.employeeCertification,
                    [name]: value,
                },
            }),
            this.validateDates,
        );
    };

    validateDates = () => {
        const { effectiveDate, expirationDate } = this.state.employeeCertification;
        let endDateError = '';

        if (
            moment(expirationDate).isValid() &&
            moment(effectiveDate).isValid() &&
            moment(expirationDate).isBefore(moment(effectiveDate))
        ) {
            endDateError = 'Expiration Date cannot be earlier than Effective Date.';
        }

        this.setState({ endDateError });
    };

    handleFileChange = (e) => {
        const [file] = e.target.files;

        if (file) {
            this.setState((state) => ({
                employeeCertification: {
                    ...state.employeeCertification,
                    attachmentFile: file,
                },
            }));
        }
    };

    onSubmit = async () => {
        if (this.state.saving) return;
        const errorMesgs = [];
        const {
            employeeCertifications, employeeCertification, endDateError, isNew, showProofUpload, certifications,
        } = this.state;

        const hasFile = isNew
            ? employeeCertification?.attachmentFile != null
            : (employeeCertification?.attachmentFile != null || employeeCertification?.normalizedFileName != null);

        const certification = certifications.find(
            (c) => c.id === employeeCertification.certificationId,
        );

        if (employeeCertifications.some((x) => x.id != employeeCertification.id && x.certificationId == employeeCertification.certificationId)) {
            toasty.error('This employee already has this certification!');
            return;
        }

        const overlaps = (aStart, aEnd, bStart, bEnd) =>
            moment(aStart).startOf('day').isSameOrBefore(
                moment(bEnd ?? '9999-12-31').endOf('day'),
            ) &&
            moment(aEnd ?? '9999-12-31').endOf('day').isSameOrAfter(
                moment(bStart).startOf('day'),
            );

        if (certification.isOperatorLevel && employeeCertifications.some((x) => x.isOperatorLevel
            && x.isGSFOperatorLevel == certification.isGSFOperatorLevel
            && x.id != employeeCertification.id
            &&
                overlaps(
                    employeeCertification.effectiveDate,
                    employeeCertification.expirationDate,
                    x.effectiveDate,
                    x.expirationDate,
                ))
        ) {
            toasty.error(`An employee can't have overlapping ${certification.isGSFOperatorLevel ? '' : 'Non '}GSF Operator level certificates`);
            return;
        }

        if (showProofUpload && !hasFile) {
            errorMesgs.push('File upload is required.');
        }
        if (endDateError) {
            errorMesgs.push(endDateError);
        }
        if (errorMesgs.length) {
            const errorsstring = errorMesgs.join(' ');
            toasty.error(errorsstring);
            return;
        }

        if (!employeeCertification.expirationDate) {
            employeeCertification.expirationDate = null;
        }

        this.setState({ errors: {}, endDateError: '', saving: true });

        const formData = new FormData();
        formData.append('EmployeeId', employeeCertification.employeeId);
        formData.append('CertificationId', employeeCertification.certificationId);
        formData.append('EffectiveDate', employeeCertification.effectiveDate);
        formData.append('ExpirationDate', employeeCertification.expirationDate || '');
        formData.append('Id', employeeCertification.id);
        if (employeeCertification.attachmentFile) {
            formData.append('AttachmentFile', employeeCertification.attachmentFile);
        }

        const url = isNew
            ? ApiRoutes.employeeCertification.create()
            : ApiRoutes.employeeCertification.update(employeeCertification.id);

        const method = isNew ? 'POST' : 'PUT';

        try {
            const response = await fetch(url, { method, body: formData });

            if (!response.ok) {
                throw new Error('Error saving certification');
            }

            const result = await response.json();
            this.onClose(result);

            const description = isNew ? 'created' : 'updated';
            toasty.success(
                `Employee Certification ${description}`,
                `Employee Certification ${description} successfully.`,
            );

            this.props.onSaveCallback();
        } catch (error) {
            this.handleSaveError(error);
        } finally {
            this.setState({ saving: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    isEndDateRequired = () => {
        const { employeeCertification, certifications } = this.state;
        if (!employeeCertification?.certificationId) return false;

        const foundCertification = certifications.find(
            (c) => c.id === employeeCertification.certificationId,
        );

        return Boolean(foundCertification?.requiresEndDate);
    };

    open = (certificationOrEmployeeId) => {
        try {
            const isNew = typeof certificationOrEmployeeId === 'number';

            let employeeCertification;

            if (isNew) {
                employeeCertification = new EmployeeCertification();
                employeeCertification.employeeId = certificationOrEmployeeId;
                employeeCertification.effectiveDate = getInputFormat(new Date());
            } else {
                employeeCertification = { ...certificationOrEmployeeId };
            }

            // Validate employeeId
            if (!employeeCertification.employeeId) {
                throw new Error('Employee ID is required');
            }

            // Format dates
            employeeCertification.effectiveDate = getInputFormat(
                employeeCertification.effectiveDate,
            );
            employeeCertification.expirationDate = getInputFormat(
                employeeCertification.expirationDate,
            );
            employeeCertification.attachmentFile = null;

            // Set state
            this.setState({
                employeeCertification,
                formTitle: isNew ? 'Add New Certification' : 'Edit Certification',
                isNew,
                fileInputKey: Date.now(),
            });

            this.showHideFieldsBasedOnCertification(
                employeeCertification.certificationId,
                this.state.certifications,
            );

            this.props.toggleShow(true);
            this.context.setFormOpened(true);
        } catch (error) {
            console.error('Error opening certification form:', error);
            toasty.error(error.message || 'Failed to open certification form');
        }
    };

    populateState = async () => {
        try {
            const certifications = await httpClient.js(ApiRoutes.certification.all());
            this.setState({ certifications, loading: false });
        } catch (error) {
            console.error('Error populating state:', error);
            this.setState({ loading: false });
        }
    };

    resetForm = () => {
        this.setState({
            formValidated: false,
            employeeCertification: new EmployeeCertification(),
            errors: {},
            endDateError: '',
            isValidated: false,
            validationMessage: null,
            saving: false,
        });
    };
    render() {
        const {
            loading,
            employeeCertification,
            certifications,
            isValidated,
            errors,
            validationMessage,
            endDateError,
            showExpirationDate,
            showProofUpload,
            fileInputKey,
            isNew,
            saving,
            formTitle,
        } = this.state;
        const { show } = this.props;

        if (!employeeCertification) return '';

        const expirationError =
            endDateError ||
            (showExpirationDate && !employeeCertification.expirationDate
                ? 'Expiration Date is required.'
                : '');

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="employeeCertificationForm"
                formIcon={faCalendarTimes}
                formTitle={formTitle}
                ref={this.formRef}
                setIsValidated={(val) => this.setState({ isValidated: val })}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onDelete={employeeCertification.id ? this.onDelete : undefined}
                errors={errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                saveDisabled={saving}
                entityId={employeeCertification.id}
            >
                <ValidatedSelect
                    className="w-auto"
                    isClearable={false}
                    id="certificationsDropdown"
                    label="Certification"
                    isDisabled={!isNew}
                    name="employeeCertification.certificationId"
                    required
                    value={
                        certifications.find((c) => c.id === employeeCertification.certificationId) ??
                        ''
                    }
                    options={certifications}
                    onChange={this.onCertificationChanged}
                    onBlur={this.onCertificationBlur}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    error="A certification is required."
                />

                <Input
                    label="Effective Date"
                    id="effectiveDate"
                    required
                    type="date"
                    className="form-control"
                    name="effectiveDate"
                    value={employeeCertification.effectiveDate || ''}
                    onChange={this.handleDateChange}
                    error="Effective Date is required."
                />

                {showExpirationDate && (
                    <Input
                        type="date"
                        id="expirationDate"
                        name="expirationDate"
                        label="Expiration Date"
                        required={showExpirationDate}
                        value={employeeCertification.expirationDate || ''}
                        onChange={this.handleDateChange}
                        error={expirationError}
                    />
                )}

                {showProofUpload && (
                    <FormGroup>
                        <FormLabel
                            htmlFor="attachmentFile"
                            label="Attachment"
                            required
                        />
                        <input
                            id="attachmentFile"
                            type="file"
                            name="attachmentFile"
                            key={fileInputKey}
                            onChange={this.handleFileChange}
                        />

                        {employeeCertification.normalizedFileName && (
                            <div className="mb-2">
                                <strong>Current File:</strong>
                                {' '}
                                {employeeCertification.normalizedFileName}
                            </div>
                        )}
                    </FormGroup>
                )}
            </SlideForm>
        );
    }
}
